import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { calculateOverallCurrentGPA, letterGradeToGPA } from '../../utils/gpa';
import { isInProgressStatus, isPendingStatus, isOverdueStatus } from '../../utils/assignments';
import Modal from '../Layout/Modal';
import { getSemesterDisplayLabel, getSemesterGroupKey, getSemesterTimelineSortKey } from '../../utils/semesters';

// Helper function to calculate GPA for a specific semester
const calculateSemesterGPA = (courses, settings) => {
  const completedCourses = (courses || []).filter(course => 
    (course.status === 'completed' || course.grade) && 
    course.grade
  );
  if (completedCourses.length === 0) return 0;

  let totalPoints = 0;
  let totalCredits = 0;

  completedCourses.forEach(course => {
    const points = letterGradeToGPA(course.grade, settings);
    const credits = parseFloat(course.credits) || 0;

    totalPoints += points * credits;
    totalCredits += credits;
  });

  return totalCredits > 0 ? totalPoints / totalCredits : 0;
};

const Container = styled.div`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${props => props.compact ? '0.125rem' : '1rem'};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  padding: ${props => props.compact ? '0.25rem' : '1.5rem'};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.backgroundSecondary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.background};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }
`;

const GPACard = styled.div`
  padding: ${props => props.compact ? '0.25rem' : '1.5rem'};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.backgroundSecondary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.background};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }
`;

const GPACardContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const GPAValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.text};
  margin-bottom: 0.25rem;
`;

const GPALabel = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 0.5rem;
`;

const GPADetails = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.textMuted};
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 0.5rem;
`;

const StatDetails = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.textMuted};
`;

const ChartContainer = styled.div`
  margin-top: 2rem;
  padding: ${props => props.compact ? '0.25rem' : '1.5rem'};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.backgroundSecondary};
`;

const ChartTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: ${props => props.theme.text};
  font-size: 1.125rem;
  font-weight: 600;
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ChartSection = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  padding: 1rem;
`;

const ChartSectionTitle = styled.h4`
  margin: 0 0 1rem 0;
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-weight: 600;
`;

const GPAChart = styled.svg`
  width: 100%;
  height: 200px;
  overflow: visible;
`;

const GPAChartContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  margin-bottom: 1rem;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.backgroundSecondary};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.textSecondary};
  }
`;

const TimelineChart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
  background: ${props => props.theme.backgroundSecondary};
  border-radius: 4px;
  border-left: 4px solid ${props => props.completed ? '#10b981' : props.active ? '#3b82f6' : '#6b7280'};
`;

const TimelineDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.completed ? '#10b981' : props.active ? '#3b82f6' : '#6b7280'};
  flex-shrink: 0;
`;

const TimelineContent = styled.div`
  flex: 1;
`;

const TimelineTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.text};
  font-size: 0.875rem;
`;

const TimelineSubtitle = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.75rem;
`;

const TimelinePagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
`;

const PaginationInfo = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.8125rem;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.disabled ? props.theme.backgroundSecondary : props.theme.background};
  color: ${props => props.theme.text};
  border-radius: 4px;
  padding: 0.45rem 0.8rem;
  font-size: 0.8125rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    background: ${props => props.disabled ? props.theme.backgroundSecondary : props.theme.backgroundSecondary};
  }
`;

const GPAProgressionChart = ({ courses, assignments, settings }) => {
  const containerRef = React.useRef(null);

  // Group courses by semester and calculate GPA for each
  const semesterData = (courses || []).reduce((acc, course) => {
    const semesterGroup = getSemesterGroupKey(course.semester);
    const key = `${course.year}-${semesterGroup}`;
    if (!acc[key]) {
      acc[key] = { semester: course.semester, semesterGroup, year: course.year, courses: [] };
    }
    acc[key].courses.push(course);
    return acc;
  }, {});

  const semesters = Object.values(semesterData)
    .sort((a, b) => {
      return getSemesterTimelineSortKey(a.semester, a.year) - getSemesterTimelineSortKey(b.semester, b.year);
    })
    .map(sem => {
      const gpa = calculateSemesterGPA(sem.courses, settings);
      return {
        ...sem,
        displaySemester: getSemesterDisplayLabel(sem.semester, settings),
        gpa
      };
    });

  // Auto-scroll to show the latest term
  React.useEffect(() => {
    if (containerRef.current && semesters.length > 0) {
      // Scroll to the right to show the latest term
      containerRef.current.scrollLeft = containerRef.current.scrollWidth;
    }
  }, [semesters]);

  if (semesters.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
        No courses found to display GPA progression
      </div>
    );
  }

  const maxGPA = 4.0;
  // Calculate chart width: minimum 6 terms visible, each term gets ~80px
  const minVisibleTerms = 6;
  const pixelsPerTerm = 80;
  const chartWidth = Math.max(minVisibleTerms * pixelsPerTerm, semesters.length * pixelsPerTerm);
  const chartHeight = 150;
  const padding = 40;

  return (
    <GPAChartContainer ref={containerRef}>
      <GPAChart viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
        {/* Grid lines */}
        {[0, 1, 2, 3, 4].map(gpa => (
          <g key={gpa}>
            <line
              x1={padding}
              y1={chartHeight - padding - (gpa / maxGPA) * (chartHeight - 2 * padding)}
              x2={chartWidth - padding}
              y2={chartHeight - padding - (gpa / maxGPA) * (chartHeight - 2 * padding)}
              stroke="#e5e7eb"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
            <text
              x={padding - 10}
              y={chartHeight - padding - (gpa / maxGPA) * (chartHeight - 2 * padding) + 4}
              textAnchor="end"
              fontSize="10"
              fill="#6b7280"
            >
              {gpa}
            </text>
          </g>
        ))}

        {/* GPA points with labels */}
        {semesters.map((sem, index) => {
          const x = padding + index * pixelsPerTerm;
          const y = chartHeight - padding - (sem.gpa / maxGPA) * (chartHeight - 2 * padding);

          return (
            <g key={`point-${index}`}>
              <circle
                cx={x}
                cy={y}
                r="6"
                fill="#3b82f6"
                stroke="#ffffff"
                strokeWidth="2"
              >
                <title>{sem.displaySemester} {sem.year}: {sem.gpa.toFixed(2)} GPA</title>
              </circle>
              {/* GPA value label */}
              <text
                x={x}
                y={y - 15}
                textAnchor="middle"
                fontSize="12"
                fontWeight="600"
                fill="#3b82f6"
              >
                {sem.gpa.toFixed(2)}
              </text>
              {/* Semester label */}
              <text
                x={x}
                y={chartHeight - 10}
                textAnchor="middle"
                fontSize="10"
                fill="#6b7280"
              >
                {sem.displaySemester} {sem.year}
              </text>
            </g>
          );
        })}

        {/* Connect the dots */}
        {semesters.length > 1 && semesters.map((sem, index) => {
          if (index === 0) return null;
          const prevX = padding + (index - 1) * pixelsPerTerm;
          const prevY = chartHeight - padding - (semesters[index - 1].gpa / maxGPA) * (chartHeight - 2 * padding);
          const x = padding + index * pixelsPerTerm;
          const y = chartHeight - padding - (sem.gpa / maxGPA) * (chartHeight - 2 * padding);

          return (
            <line
              key={`line-${index}`}
              x1={prevX}
              y1={prevY}
              x2={x}
              y2={y}
              stroke="#3b82f6"
              strokeWidth="2"
            />
          );
        })}
      </GPAChart>
    </GPAChartContainer>
  );
};

const CourseCompletionTimeline = ({ courses, settings, pageSize = null }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const sortedCourses = [...courses].sort((a, b) => {
    return getSemesterTimelineSortKey(b.semester, b.year) - getSemesterTimelineSortKey(a.semester, a.year);
  });

  const enabledPageSize = Number.isFinite(pageSize) ? pageSize : null;
  const totalPages = enabledPageSize ? Math.ceil(sortedCourses.length / enabledPageSize) : 1;
  const paginatedCourses = enabledPageSize
    ? sortedCourses.slice((currentPage - 1) * enabledPageSize, currentPage * enabledPageSize)
    : sortedCourses;

  useEffect(() => {
    setCurrentPage(1);
  }, [courses, enabledPageSize]);

  if (sortedCourses.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
        No courses found to display timeline
      </div>
    );
  }

  const showPagination = Boolean(enabledPageSize && sortedCourses.length > enabledPageSize);

  return (
    <>
      {showPagination && (
        <TimelinePagination>
          <PaginationInfo>
            Showing {((currentPage - 1) * enabledPageSize) + 1}-{Math.min(currentPage * enabledPageSize, sortedCourses.length)} of {sortedCourses.length}
          </PaginationInfo>
          <PaginationControls>
            <PaginationButton
              type="button"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>
            <PaginationButton
              type="button"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </PaginationButton>
          </PaginationControls>
        </TimelinePagination>
      )}
      <TimelineChart>
        {paginatedCourses.map((course) => {
          const isCompleted = course.status === 'completed';
          const isActive = course.status === 'active';
          const grade = course.grade || 'N/A';

          return (
            <TimelineItem key={course._id} completed={isCompleted} active={isActive}>
              <TimelineDot completed={isCompleted} active={isActive} />
              <TimelineContent>
                <TimelineTitle>{course.name}</TimelineTitle>
                <TimelineSubtitle>
                  {getSemesterDisplayLabel(course.semester, settings)} {course.year} • Grade: {grade} • {course.credits} credits
                </TimelineSubtitle>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </TimelineChart>
    </>
  );
};

const AcademicAnalytics = ({ compact }) => {
  const { assignments, courses, settings } = useData();
  const { user } = useAuth();
  const [accountStats, setAccountStats] = useState({
    averageGrade: 'N/A',
    assignmentsDue: 0
  });
  const [showGPAHelp, setShowGPAHelp] = useState(false);

  useEffect(() => {
    if (!courses || !assignments || !user) return;

    // Calculate credit-weighted GPA using ALL courses and assignments (account-wide)
    const averageGPA = calculateOverallCurrentGPA(courses, assignments, settings);

    // Assignments still due (in progress + pending from all active courses)
    const inProgress = (assignments || []).filter(a => isInProgressStatus(a.status)).length;
    const pending = (assignments || []).filter(a => isPendingStatus(a.status)).length;
    const assignmentsDue = inProgress + pending;

    const hasGrades = courses.some(c => c.grade) || assignments.some(a => a.grade);
    setAccountStats({
      averageGrade: hasGrades ? averageGPA.toFixed(2) : 'N/A',
      assignmentsDue
    });
  }, [courses, assignments, user, settings]);

  // Calculate completion rate for all assignments
  const totalAssignments = (assignments || []).length;
  const completedAssignments = (assignments || []).filter(a => a.status === 'completed').length;
  const completionRate = totalAssignments > 0 ? Math.round((completedAssignments / totalAssignments) * 100) : 0;

  // Calculate overdue assignments
  const overdueAssignments = (assignments || []).filter(a => isOverdueStatus(a.status)).length;

  // Calculate total courses and active courses
  const totalCourses = (courses || []).length;
  const activeCourses = (courses || []).filter(c => c.status !== 'completed').length;
  const completedCourses = (courses || []).filter(c => c.status === 'completed').length;

  // Calculate average assignments per course
  const avgAssignmentsPerCourse = totalCourses > 0 ? Math.round(totalAssignments / totalCourses) : 0;

  return (
    <Container compact={compact}>
      <Grid>
        <GPACard compact={compact}>
          <GPACardContent>
            <div>
              <GPALabel>Overall GPA</GPALabel>
              <GPAValue>{accountStats.averageGrade}</GPAValue>
              <GPADetails>Across all completed courses</GPADetails>
            </div>
          </GPACardContent>
        </GPACard>

        <StatCard compact={compact}>
          <StatLabel>Total Assignments</StatLabel>
          <StatValue>{totalAssignments}</StatValue>
          <StatDetails>All-time assignment count</StatDetails>
        </StatCard>

        <StatCard compact={compact}>
          <StatLabel>Completion Rate</StatLabel>
          <StatValue>{completionRate}%</StatValue>
          <StatDetails>{completedAssignments} of {totalAssignments} assignments</StatDetails>
        </StatCard>

        <StatCard compact={compact}>
          <StatLabel>Overdue Assignments</StatLabel>
          <StatValue>{overdueAssignments}</StatValue>
          <StatDetails>Need immediate attention</StatDetails>
        </StatCard>

        <StatCard compact={compact}>
          <StatLabel>Total Courses</StatLabel>
          <StatValue>{totalCourses}</StatValue>
          <StatDetails>{activeCourses} active, {completedCourses} completed</StatDetails>
        </StatCard>

        <StatCard compact={compact}>
          <StatLabel>Avg Assignments/Course</StatLabel>
          <StatValue>{avgAssignmentsPerCourse}</StatValue>
          <StatDetails>Overall workload average</StatDetails>
        </StatCard>
      </Grid>

      <ChartContainer compact={compact}>
        <ChartTitle>Academic Progress Overview</ChartTitle>
        <ChartGrid>
          <ChartSection>
            <ChartSectionTitle>GPA Progression</ChartSectionTitle>
            <GPAProgressionChart courses={courses} assignments={assignments} settings={settings} />
          </ChartSection>
          <ChartSection>
            <ChartSectionTitle>Course Timeline</ChartSectionTitle>
            <CourseCompletionTimeline courses={courses} settings={settings} pageSize={5} />
          </ChartSection>
        </ChartGrid>
      </ChartContainer>

      <Modal
        isOpen={showGPAHelp}
        onClose={() => setShowGPAHelp(false)}
        title="Overall GPA Calculation Help"
      >
        <p>Your overall GPA is calculated across all courses and assignments in your academic history.</p>
        <p>This includes both current and completed courses to give you a comprehensive view of your academic performance.</p>
      </Modal>
    </Container>
  );
};

export { GPAProgressionChart, CourseCompletionTimeline };
export default AcademicAnalytics;